import loglevel from 'loglevel';
import autobind from 'class-autobind';

import keys from 'lodash/keys';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';

import { sendRequest } from 'zoomforth_zfutils/xhr_tools.js';
import { reportingDatetimeFormat } from 'zoomforth_zfutils/datetime_formatting.js';

import userStore from 'zoomforth_zf/appglobals/user.js';
import pagesStore from 'zoomforth_zf/appglobals/pages.js';
import { get as intercomTracker } from 'zoomforth_zf/appglobals/intercom-tracker.js';

import { PAGE_HISTORY_EVENT_TYPES } from 'const_shared.js';


const isNumeric = n =>
  !isNaN(parseFloat(n)) && isFinite(n);


class PageHistoryTracker {

  constructor(pageDataOrId) {
    this.page = isNumeric(pageDataOrId)
      ? pagesStore.get().get(pageDataOrId).toJSON()
      : pageDataOrId;

    if (!this.page) {
      throw new Error('PageHistoryTracker needs a valid page object. You need to pass it in or init the pagesStore to find one by ID.');
    }

    this.user = userStore.get().toJSON();

    autobind(this);
  }

  isValidEventDict(eventDict) {
    const eventType = eventDict.event_type;
    const eventSubtype = eventDict.event_subtype;

    const validPageHistoryEventTypes = keys(PAGE_HISTORY_EVENT_TYPES);

    if (!includes(validPageHistoryEventTypes, eventType)) {
      loglevel.error(`eventType "${eventType}" is not a valid page history event type`);
      return false;
    }

    if (!isNil(eventSubtype) && !includes(PAGE_HISTORY_EVENT_TYPES[eventType]['subtypes'], eventSubtype)) {
      loglevel.error(`eventSubtype "${eventSubtype}" is not a valid page history event subtype`);
      return false;
    }

    return true;
  }

  saveAndTrackEvent(event, saveDict) {
    const pageData = {
      page_id: this.page.id
    };

    intercomTracker().trackEvent(event, pageData);

    // validate and warn before sending to the server
    if (!this.isValidEventDict(saveDict)) {
      return;
    }

    sendRequest({

      url: '/api/page_history_events',
      method: 'POST',
      params: {
        ...saveDict,
        ...pageData
      }

    });

  }

  trackCreatePage() { 
    this.saveAndTrackEvent('Created Page', {
      event_type: 'create_page',
      description: `${this.user.name} created the page "${this.page.name}"`
    });
  }

  trackCreatePageFromTemplate(newPageName) {
    this.saveAndTrackEvent('Created Page from template', {
      event_type: 'create_page',
      event_subtype: 'from_template',
      description: `${this.user.name} created the page "${newPageName}" from "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackPublishPage() { 
    this.saveAndTrackEvent('Published Page', {
      event_type: 'publish_page',
      description: `${this.user.name} published the page "${this.page.name}"`
    });
  }

  trackApproveAndPublish() {
    this.saveAndTrackEvent('Approved Page', {
      event_type: 'publish_page',
      event_subtype: 'with_approve',
      description: `${this.user.name} approved the publication request for the page "${this.page.name}"`
    });
  }

  trackRequestApproval() { 
    this.saveAndTrackEvent('Requested Page Approval', {
      event_type: 'approval_cycle_event',
      event_subtype: 'request_approval',
      description: `${this.user.name} requested approval to publish the page ${this.page.name}`
    });
  }

  trackDeclineApproval() {
    this.saveAndTrackEvent('Declined Approval Request', {
      event_type: 'approval_cycle_event',
      event_subtype: 'decline_approval',
      description: `${this.user.name} rejected the publication request for the page "${this.page.name}"`
    });
  }

  trackDeletePage() {
    this.saveAndTrackEvent('Deleted Page', {
      event_type: 'delete_page',
      description: `${this.user.name} deleted the page "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackArchivePage() {
    this.saveAndTrackEvent('Archived Page', {
      event_type: 'archive_page',
      description: `${this.user.name} archived the page "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackUnarchivePage() {
    this.saveAndTrackEvent('Unarchived Page', {
      event_type: 'unarchive_page',
      description: `${this.user.name} unarchived the page "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackReactivatePage() {
    this.saveAndTrackEvent('Reactivated Page', {
      event_type: 'reactivate_page',
      description: `${this.user.name} reactivated the page "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackDeactivatePage() {
    this.saveAndTrackEvent('Deactivated Page', {
      event_type: 'deactivate_page',
      description: `${this.user.name} deactivated the page "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackScheduledDeactivationForSite(date) {
    this.saveAndTrackEvent('Scheduled Deactivation for Site', {
      event_type: 'schedule_site_deactivation',
      description: `${this.user.name} scheduled deactivation for the site "${this.page.name}" on ${reportingDatetimeFormat(date)}`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackRemovedScheduledDeactivationForSite() {
    this.saveAndTrackEvent('Removed Scheduled Deactivation for Site', {
      event_type: 'remove_scheduled_site_deactivation',
      description: `${this.user.name} removed the scheduled deactivation for the site "${this.page.name}"`,
      target_object_type: 'simple_lens',
      target_object_id: this.page.id
    });
  }

  trackAddPageComment() {
    this.saveAndTrackEvent('Commented on Page', {
      event_type: 'edit_metadata',
      event_subtype: 'page_comment',
      description: `${this.user.name} commented on the page "${this.page.name}"`
    });
  }

  trackChangeVisitorAccessSetting(visitorAccessSetting) { 
    this.saveAndTrackEvent(`Changed Page visitor access setting to "${visitorAccessSetting}"`, {
      event_type: 'edit_metadata',
      event_subtype: 'change_visitor_access_settings',
      description: `${this.user.name} changed the visitor access setting to "${visitorAccessSetting}"`
    });
  }

  trackChangeVisitorAccessPassword() { 
    this.saveAndTrackEvent('Changed Page visitor access password', {
      event_type: 'edit_metadata',
      event_subtype: 'change_visitor_access_password',
      description: `${this.user.name} changed the visitor access password`
    });
  }

  trackAddMediaLibraryAssetToGridInSubpage(subpageName) {
    this.saveAndTrackEvent('Added media to a Page', {
      event_type: 'add_to_media_gallery',
      event_subtype: 'from_asset',
      description: `${this.user.name} added media to the "${subpageName}" subpage`
    });
  }

  trackAddMediaLibraryUploadToSubpage(subpageName) {
    this.saveAndTrackEvent('Uploaded asset to a Page', {
      event_type: 'add_to_media_gallery',
      event_subtype: 'from_upload',
      description: `${this.user.name} uploaded a file to the "${subpageName}" subpage`
    });
  }

  trackAddSubpage(subpageName) {
    this.saveAndTrackEvent('Added new subpage to a page', {
      event_type: 'edit_page_sections',
      event_subtype: 'add_page_section',
      description: `${this.user.name} added a new subpage: "${subpageName}"`
    });
  }

  trackDeleteSubpage(subpageName) {
    this.saveAndTrackEvent('Deleted subpage from a page', {
      event_type: 'edit_page_sections',
      event_subtype: 'delete_page_section',
      description: `${this.user.name} deleted the subpage "${subpageName}"`
    });
  }
  
  trackAddSection(subpageName, sectionType) {
    this.saveAndTrackEvent('Added new section to a page', {
      event_type: 'edit_page_sections',
      event_subtype: 'add_codesbane_section',
      description: `${this.user.name} added a new "${sectionType}" section to subpage "${subpageName}"`
    });
  }

  trackDeleteSection(subpageName, sectionType) {
    this.saveAndTrackEvent('Deleted section from a page', {
      event_type: 'edit_page_sections',
      event_subtype: 'delete_codesbane_section',
      description: `${this.user.name} deleted a "${sectionType}" section from subpage "${subpageName}"`
    });
  }

  trackAddMenuAnchorLink(contentUuid, anchorLinkName) {
    this.saveAndTrackEvent('Added a menu anchor link', {
      event_type: 'edit_page_sections',
      event_subtype: 'add_menu_anchor_link',
      description: `${this.user.name} added a new section anchor link called "${anchorLinkName}"`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackAddContent(contentUuid, contentType, subpageName) {
    this.saveAndTrackEvent(`Added a ${contentType} tile to a page`, {
      event_type: 'add_tile',
      event_subtype: `${contentType}_tile`,
      description: `${this.user.name} added a ${contentType} tile to "${subpageName}"`,
      target_object_type: contentType,
      target_object_id: contentUuid
    });
  }

  /* for now we can't know which tile was actually moved/resized. Oh well. */ 
  trackMoveOrResizeTile() {
    this.saveAndTrackEvent('Moved or resized a tile', {
      event_type: 'page_content_edit',
      event_subtype: 'move_tile',
      description: `${this.user.name} dragged or resized a tile`
    });
  }

  trackCopyTile(contentType, contentUuid) {
    this.saveAndTrackEvent('Copied a tile', {
      event_type: 'page_content_edit',
      event_subtype: 'copy_tile',
      description: `${this.user.name} copied a ${contentType} tile`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackDeleteTile(contentType, contentUuid) {
    this.saveAndTrackEvent('Deleted a tile', {
      event_type: 'page_content_edit',
      event_subtype: 'delete_tile',
      description: `${this.user.name} deleted a ${contentType} tile`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackDiscardChanges() {
    this.saveAndTrackEvent('Discarded unpublished changes', {
      event_type: 'page_content_edit',
      event_subtype: 'discard_changes',
      description: `${this.user.name} discarded unpublished changes`
    });
  }

  // @TODO add to existing editor
  trackReplaceTile(contentType, contentUuid) {
    this.saveAndTrackEvent('Replaced a tile', {
      event_type: 'page_content_edit',
      event_subtype: 'replace_tile',
      description: `${this.user.name} replaced a tile with a ${contentType} tile`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackChangeTheme(themeId, themeName) {
    this.saveAndTrackEvent('Changed Page theme', {
      event_type: 'change_theme',
      description: `${this.user.name} changed the page theme to "${themeName}"`,
      target_object_type: 'lens_style',
      target_object_id: themeId
    });
  }

  trackChangePageTitle(pageTitle) {
    this.saveAndTrackEvent('Changed Page title', {
      event_type: 'change_page_title',
      description: `${this.user.name} changed the page title to "${pageTitle}"`
    });
  }

  trackChangeColumnCount(sectionUuid, newColumnCount) {
    this.saveAndTrackEvent('Changed section column count', {
      event_type: 'change_column_count',
      description: `${this.user.name} changed the number of grid columns to ${newColumnCount}`,
      target_object_type: 'simple_lens',
      target_object_id: sectionUuid
    });
  }

  trackReplaceTileAsset(contentType, contentUuid) {
    this.saveAndTrackEvent(`Replaced the tile asset of a ${contentType} tile`, {
      event_type: 'tile_edit',
      event_subtype: 'replace_tile_asset',
      description: `${this.user.name} changed the asset of a ${contentType} tile`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  // @TODO add to existing editor
  trackCropDisplayImage(contentUuid) {
    this.saveAndTrackEvent('Cropped a display image', {
      event_type: 'tile_edit',
      event_subtype: 'crop_display_image',
      description: `${this.user.name} cropped a display image`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackChangeVideoDisplayImage(contentUuid) {
    this.saveAndTrackEvent('Changed the display image of a video', {
      event_type: 'tile_edit',
      event_subtype: 'change_display_image',
      description: `${this.user.name} changed a display image of a video`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackChangeVideoDisplayImageFromLibrary(contentUuid) { 
    this.saveAndTrackEvent('Selected a display image from the Media Library', {
      event_type: 'tile_edit',
      event_subtype: 'change_display_image_from_library',
      description: `${this.user.name} swapped a video display image for another from the Media Library`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackTileContentChange(contentUuid) {
    this.saveAndTrackEvent('Changed tile content (title, caption, description, or text)', {
      event_type: 'tile_edit',
      event_subtype: 'change_tile_content',
      description: `${this.user.name} changed tile content (title, caption, description, or text)`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackChangeTileAction(contentUuid, tileActionName) {
    this.saveAndTrackEvent('Changed a tile action', {
      event_type: 'tile_edit',
      event_subtype: 'change_tile_action',
      description: `${this.user.name} changed the tile action to "${tileActionName}"`,
      target_object_type: 'lens_content',
      target_object_id: contentUuid
    });
  }

  trackCreateRoute() {
    this.saveAndTrackEvent('Created new link', {
      event_type: 'create_route',
      description: `${this.user.name} created a new link`
    });
  }

  trackDeleteRoute() {
    this.saveAndTrackEvent('Deleted link', {
      event_type: 'delete_route',
      description: `${this.user.name} deleted a link`
    });
  }

}

export default PageHistoryTracker;
